import type { QueuedMutation } from './offlineQueue';

/**
 * «Entrega pendiente» del recorrido (P2, QA T6b del 15-oct).
 *
 * «Guardar y entregar» son dos pasos: cerrar el tramo y, cuando el servidor
 * responde, entregar el recorrido. CON red, el segundo vivía solo en memoria:
 * una recarga (F5, o la app que muere) en el segundo siguiente dejaba el tramo
 * cerrado y el recorrido `paused`, sin aviso, con el operario convencido de
 * haberlo entregado (×3 en el banco). Sin red no pasaba: la cola persistente
 * guarda los dos.
 *
 * La intención se guarda ANTES de cerrar el tramo, en `localStorage`
 * (síncrono: llega a disco aunque la recarga sea inmediata), y se borra al
 * acabar el flujo. Si una carga posterior de la app la encuentra, la completa
 * cuando el tramo ya está cerrado (`resumeActionFor`). `finishRoute` repetido
 * no hace nada, así que una entrega doble es inocua.
 */
export interface PendingRouteDelivery {
  routeId: string;
  /** El tramo que se cerraba: decide si el cierre llegó al servidor. */
  segmentId: string;
  userId?: string | null;
  /** Hora del gesto: la entrega la lleva como fin del recorrido. */
  registerEndDate: string;
  createdAt: number;
  /** Carga de la página que la guardó: esa misma no la retoma. */
  sessionId: string;
}

const STORAGE_KEY = 'rois_pending_route_delivery';
/** Pasado esto ya no se completa: el recorrido lo habrá mirado alguien. */
const MAX_AGE_MS = 24 * 3_600_000;

/** Identifica ESTA carga de la página (cambia con cada recarga). */
export const PAGE_SESSION_ID = `${Date.now()}-${Math.random()
  .toString(36)
  .slice(2)}`;

const read = (): PendingRouteDelivery[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (entries: PendingRouteDelivery[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Sin almacenamiento la entrega sigue su camino en memoria, como antes.
  }
};

export function rememberRouteDelivery(
  entry: Omit<PendingRouteDelivery, 'createdAt' | 'sessionId'>,
  now: number = Date.now()
): void {
  write([
    ...read().filter((d) => d.routeId !== entry.routeId),
    { ...entry, createdAt: now, sessionId: PAGE_SESSION_ID },
  ]);
}

export function forgetRouteDelivery(routeId: string): void {
  write(read().filter((d) => d.routeId !== routeId));
}

/**
 * Las que hay que retomar: de OTRA carga de la página (las de esta las cierra
 * su propio flujo), de esta cuenta y de menos de 24 h. Las viejas se limpian.
 */
export function deliveriesToResume(
  userId: string | undefined | null,
  now: number = Date.now()
): PendingRouteDelivery[] {
  const all = read();
  const fresh = all.filter((d) => now - d.createdAt <= MAX_AGE_MS);
  if (fresh.length !== all.length) write(fresh);
  return fresh.filter(
    (d) => d.sessionId !== PAGE_SESSION_ID && (!d.userId || d.userId === userId)
  );
}

export type ResumeAction = 'deliver' | 'wait' | 'drop';

const inputOf = (mutation: Pick<QueuedMutation, 'variables'>) =>
  ((mutation.variables as { input?: Record<string, unknown> } | undefined)
    ?.input ?? {}) as Record<string, unknown>;

/**
 * Qué hacer con una entrega pendiente, sabiendo el tramo abierto del
 * recorrido en el servidor y lo que hay en la cola sin red.
 *
 * 🔴 Nunca se entrega mientras la cola tenga algo de ese recorrido por
 * aplicar (su cierre, un tramo por crear): el servidor rechazaría lo encolado
 * con el recorrido ya entregado y la cola lo DESCARTARÍA al sincronizar.
 */
export function resumeActionFor(
  entry: Pick<PendingRouteDelivery, 'routeId' | 'segmentId'>,
  serverActiveSegmentId: string | null,
  queue: Array<Pick<QueuedMutation, 'operationName' | 'variables'>>
): ResumeAction {
  if (
    queue.some(
      (m) =>
        m.operationName === 'FinishSurveillanceRoute' &&
        inputOf(m).id === entry.routeId
    )
  ) {
    return 'drop';
  }
  if (
    queue.some(
      (m) =>
        inputOf(m).id === entry.segmentId ||
        inputOf(m).surveillanceRouteId === entry.routeId
    )
  ) {
    return 'wait';
  }
  // Tramo cerrado en el servidor: lo que faltaba era entregar.
  if (!serverActiveSegmentId) return 'deliver';
  // Sigue abierto: el cierre no llegó (el operario lo ve abierto). U otro
  // tramo abierto: siguió trabajando. En los dos casos, no entregar.
  return 'drop';
}
